import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { ID, ProjectAlert } from '@proofroom/schema';
import { Header } from '../components/Header';
import { AgencyRail, useIsAgency } from '../components/AgencyRail';
import { Switch } from '../components/Switch';
import { EmptyState, ErrorState, Spinner } from '../components/ui';
import { useProjectDocuments, useProjects } from '../api/queries';
import { documents, notifications } from '../api/endpoints';
import { useSession } from '../auth/session';
import { Activity, SortSelect, sortRecords, type ListSort } from '../components/Activity';
import { DocumentTags } from '../components/DocumentTags';
import { ApiError } from '../api/client';

/**
 * The signed-in user's own switch for this project, the same one the Notifications screen
 * lists. Nothing is shown until the settings arrive, and nothing at all when they do not name
 * the project (they are read from the same membership the page itself is gated on).
 */
function ProjectAlertSwitch({ projectId }: { projectId: ID }) {
  const [alert, setAlert] = useState<ProjectAlert | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setAlert(null);
    notifications.settings()
      .then((s) => { if (alive) setAlert(s.projects.find((p) => p.projectId === projectId) ?? null); })
      .catch(() => { /* the page stands without the switch */ });
    return () => { alive = false; };
  }, [projectId]);

  const flip = async (on: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const next = await notifications.setProjectMuted(projectId, !on);
      setAlert(next.projects.find((p) => p.projectId === projectId) ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the switch');
    } finally {
      setBusy(false);
    }
  };

  if (!alert) return null;
  return (
    <span className="btn-row" style={{ gap: 6 }}>
      <span className="muted small">Email alerts for this project:</span>
      <Switch label="Email alerts for this project" checked={!alert.muted} busy={busy} onChange={(on) => void flip(on)} />
      {error && <span className="error-text small" role="alert">{error}</span>}
    </span>
  );
}

export function Documents() {
  const { projectId = '' } = useParams();
  const projects = useProjects();
  const q = useProjectDocuments(projectId);
  const project = projects.data?.find((p) => p.id === projectId);
  const agency = useIsAgency();
  const { user } = useSession();
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState('');
  const [sort, setSort] = useState<ListSort>('updated');
  const [attention, setAttention] = useState(false);
  const [limit, setLimit] = useState(25);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const tags = [...new Set((q.data ?? []).flatMap((d) => d.tags ?? []))].sort((a, b) => a.localeCompare(b));
  const filtered = sortRecords((q.data ?? []).filter((d) => (!tag || d.tags?.includes(tag)) &&
    (!attention || (d.review?.outstanding ?? 0) > 0) && [d.name, ...(d.tags ?? [])].join(' ').toLowerCase().includes(search.toLowerCase())), sort);
  const shown = filtered.slice(0, limit);
  return (
    <div className="app">
      <Header crumbs={[{ label: 'Clients', to: '/projects' }, { label: project?.clientName ?? 'Unassigned client', to: `/projects?client=${project?.clientOrgId ?? 'unassigned'}` }, { label: project?.name ?? 'Project' }]} />
      <main className="main">
        <div className={agency ? 'page with-rail' : 'page'}>
          {agency && <AgencyRail />}
          <div className="page-body">
            <div className="page-head">
              <h1>{project?.name ?? 'Documents'}</h1>
              <span className="btn-row" style={{ gap: 16 }}>
                <span className="sub">{q.data ? `${q.data.length} document${q.data.length === 1 ? '' : 's'}` : ''}</span>
                {projectId && <ProjectAlertSwitch projectId={projectId} />}
              </span>
            </div>
            {project?.beatJobNumber && <p className="beat-link">Beat job <strong>{project.beatJobNumber}</strong>{project.beatJobUrl && <> · <a href={project.beatJobUrl} target="_blank" rel="noreferrer">Open in Beat ↗</a></>}</p>}
            {!project?.beatJobNumber && project?.beatJobUrl && <p className="beat-link"><a href={project.beatJobUrl} target="_blank" rel="noreferrer">Open in Beat ↗</a></p>}
            <div className="list-controls">
              <label className="list-field">Search<input className="input" type="search" placeholder="Document or tag" value={search} onChange={(e) => { setSearch(e.target.value); setLimit(25); }} /></label>
              <label className="list-field">Tag<select className="input" aria-label="Filter by tag" value={tag} onChange={(e) => { setTag(e.target.value); setLimit(25); }}><option value="">All tags</option>{tags.map((t) => <option key={t}>{t}</option>)}</select></label>
              <SortSelect value={sort} onChange={(s) => { setSort(s); setLimit(25); }} />
              <label><input type="checkbox" checked={attention} onChange={(e) => { setAttention(e.target.checked); setLimit(25); }} /> Needs attention</label>
            </div>
            <p className="muted small" style={{ marginBottom: 12 }}>Review totals include copy amends and comment threads across all drafts. Accepted copy amends remain outstanding until applied.</p>
            {error && <p className="error-text" role="alert">{error}</p>}
            {q.isPending && <Spinner label="Loading documents" />}
            {q.isError && <ErrorState title="Could not load documents" error={q.error} retry={() => void q.refetch()} />}
            {q.data && q.data.length === 0 && <EmptyState title="No documents yet">Documents appear here when a designer publishes a draft from InDesign.</EmptyState>}
            {q.data && q.data.length > 0 && (
              <div className="card list">
                {shown.map((d) => (
                  <div key={d.id} className="row">
                    <div className="grow">
                      <Link className="title" to={d.currentDraftId ? `/documents/${d.id}/drafts/${d.currentDraftId}` : `/documents/${d.id}`}>{d.name}</Link>
                      <div className="meta">{d.currentDraftId ? 'Current draft ready' : 'No draft published'}{d.approvedDraftNumber !== undefined ? ` · Approved: draft ${d.approvedDraftNumber}` : ''}</div>
                      <Activity record={d} />
                      {d.review && <div className="review-summary">
                        <span>{d.review.total} amends / threads</span>
                        <span className={d.review.outstanding ? 'review-warning' : ''}>{d.review.outstanding} outstanding</span>
                        {d.review.unansweredQuestions > 0 && <span className="review-warning">{d.review.unansweredQuestions} unanswered question{d.review.unansweredQuestions === 1 ? '' : 's'}</span>}
                        {d.review.total > 0 && d.review.outstanding === 0 && <span className="review-resolved">All resolved</span>}
                        {d.review.total === 0 && <span className="muted">No amends raised</span>}
                      </div>}
                      <DocumentTags document={d} onSaved={() => void q.refetch()} />
                    </div>
                    {user?.role === 'admin' && <button className="btn sm danger" disabled={deleting === d.id} onClick={async () => {
                      if (!window.confirm(`Delete ${d.name}, all its drafts, comments and shared links? This cannot be undone.`)) return;
                      setDeleting(d.id); setError('');
                      try { await documents.remove(d.id); await q.refetch(); await projects.refetch(); }
                      catch (err) { setError(err instanceof Error ? err.message : 'Could not delete document'); }
                      finally { setDeleting(null); }
                    }}>Delete</button>}
                    <Link to={`/documents/${d.id}`} className="btn sm ghost">Drafts</Link>
                    {d.currentDraftId && <Link to={`/documents/${d.id}/drafts/${d.currentDraftId}`} className="btn sm primary">Open</Link>}
                  </div>
                ))}
              </div>
            )}
            {q.data && filtered.length === 0 && q.data.length > 0 && <EmptyState title="No matching documents">Change your search or filters.</EmptyState>}
            {filtered.length > limit && <button className="btn" onClick={() => setLimit(limit + 25)}>Show more documents ({filtered.length - limit} remaining)</button>}
          </div>
        </div>
      </main>
    </div>
  );
}
